/**
 * Our own names for the airports travellers on this site actually use, shown
 * ahead of the supplier's.
 *
 * TBO's `AirportName` is uneven. A new airport is filed under its bare code for
 * a while (Noida came back as "DXN"), the busiest ones are shortened ("Indira
 * Gandhi Airport"), many are just the city again ("Bhopal"), and some are
 * missing. The card line and the airport filter both exist to tell one airport
 * from another, so a row that reads "DXN" or repeats the city says nothing.
 *
 * This covers India's scheduled airports and the foreign ones Indian travellers
 * fly to most - which is most of what this site sells. Anything not listed
 * falls back to the supplier's name, then to "<City> Airport"; see
 * `airportDisplayName`.
 *
 * Official names, kept to what fits a line: "International Airport" is spelled
 * out because that is how people know them, honorifics are kept as the airport
 * itself writes them.
 */
export const AIRPORT_NAMES: Readonly<Record<string, string>> = {
  // ── India: metros ───────────────────────────────────────────────────────
  DEL: 'Indira Gandhi International Airport',
  DXN: 'Noida International Airport',
  HDO: 'Hindon Airport',
  BOM: 'Chhatrapati Shivaji Maharaj International Airport',
  NMI: 'Navi Mumbai International Airport',
  BLR: 'Kempegowda International Airport',
  MAA: 'Chennai International Airport',
  CCU: 'Netaji Subhas Chandra Bose International Airport',
  HYD: 'Rajiv Gandhi International Airport',
  AMD: 'Sardar Vallabhbhai Patel International Airport',
  PNQ: 'Pune International Airport',
  GOI: 'Goa Dabolim International Airport',
  GOX: 'Manohar International Airport (Mopa)',
  COK: 'Cochin International Airport',

  // ── India: north ────────────────────────────────────────────────────────
  JAI: 'Jaipur International Airport',
  LKO: 'Chaudhary Charan Singh International Airport',
  VNS: 'Lal Bahadur Shastri International Airport',
  AYJ: 'Maharishi Valmiki International Airport',
  IXD: 'Prayagraj Airport',
  KNU: 'Kanpur Airport',
  GOP: 'Gorakhpur Airport',
  AGR: 'Agra Airport',
  ATQ: 'Sri Guru Ram Dass Jee International Airport',
  IXC: 'Shaheed Bhagat Singh International Airport',
  SXR: 'Srinagar International Airport',
  IXJ: 'Jammu Airport',
  IXL: 'Kushok Bakula Rimpochee Airport',
  DED: 'Jolly Grant Airport',
  DHM: 'Kangra Airport',
  KUU: 'Kullu Manali Airport',
  UDR: 'Maharana Pratap Airport',
  JDH: 'Jodhpur Airport',
  JSA: 'Jaisalmer Airport',

  // ── India: west and centre ──────────────────────────────────────────────
  STV: 'Surat International Airport',
  BDQ: 'Vadodara Airport',
  RAJ: 'Rajkot International Airport',
  BHJ: 'Bhuj Airport',
  NAG: 'Dr. Babasaheb Ambedkar International Airport',
  IDR: 'Devi Ahilyabai Holkar Airport',
  BHO: 'Raja Bhoj Airport',
  JLR: 'Jabalpur Airport',
  GWL: 'Gwalior Airport',
  RPR: 'Swami Vivekananda Airport',
  IXU: 'Aurangabad Airport',
  ISK: 'Nashik Airport',
  KLH: 'Kolhapur Airport',
  SAG: 'Shirdi International Airport',

  // ── India: south ────────────────────────────────────────────────────────
  TRV: 'Trivandrum International Airport',
  CCJ: 'Calicut International Airport',
  CNN: 'Kannur International Airport',
  IXE: 'Mangaluru International Airport',
  MYQ: 'Mysuru Airport',
  HBX: 'Hubballi Airport',
  IXG: 'Belagavi Airport',
  CJB: 'Coimbatore International Airport',
  IXM: 'Madurai International Airport',
  TRZ: 'Tiruchirappalli International Airport',
  TCR: 'Tuticorin Airport',
  VTZ: 'Visakhapatnam International Airport',
  VGA: 'Vijayawada International Airport',
  TIR: 'Tirupati International Airport',
  RJA: 'Rajahmundry Airport',
  PNY: 'Puducherry Airport',

  // ── India: east, north-east and islands ─────────────────────────────────
  PAT: 'Jay Prakash Narayan International Airport',
  GAY: 'Gaya International Airport',
  DBR: 'Darbhanga Airport',
  IXR: 'Birsa Munda Airport',
  DGH: 'Deoghar Airport',
  BBI: 'Biju Patnaik International Airport',
  JRG: 'Veer Surendra Sai Airport',
  IXB: 'Bagdogra International Airport',
  GAU: 'Lokpriya Gopinath Bordoloi International Airport',
  DIB: 'Dibrugarh Airport',
  JRH: 'Jorhat Airport',
  IXS: 'Silchar Airport',
  IMF: 'Bir Tikendrajit International Airport',
  IXA: 'Maharaja Bir Bikram Airport',
  AJL: 'Lengpui Airport',
  DMU: 'Dimapur Airport',
  SHL: 'Shillong Airport',
  IXZ: 'Veer Savarkar International Airport',

  // ── Abroad: the Gulf and West Asia ──────────────────────────────────────
  DXB: 'Dubai International Airport',
  DWC: 'Al Maktoum International Airport',
  SHJ: 'Sharjah International Airport',
  AUH: 'Zayed International Airport',
  DOH: 'Hamad International Airport',
  MCT: 'Muscat International Airport',
  BAH: 'Bahrain International Airport',
  KWI: 'Kuwait International Airport',
  RUH: 'King Khalid International Airport',
  JED: 'King Abdulaziz International Airport',
  DMM: 'King Fahd International Airport',
  IST: 'Istanbul Airport',
  SAW: 'Sabiha Gökçen International Airport',

  // ── Abroad: Asia Pacific ────────────────────────────────────────────────
  SIN: 'Singapore Changi Airport',
  BKK: 'Suvarnabhumi Airport',
  DMK: 'Don Mueang International Airport',
  HKT: 'Phuket International Airport',
  KUL: 'Kuala Lumpur International Airport',
  DPS: 'Ngurah Rai International Airport',
  CGK: 'Soekarno-Hatta International Airport',
  HAN: 'Noi Bai International Airport',
  SGN: 'Tan Son Nhat International Airport',
  MNL: 'Ninoy Aquino International Airport',
  HKG: 'Hong Kong International Airport',
  HND: 'Tokyo Haneda Airport',
  NRT: 'Narita International Airport',
  ICN: 'Incheon International Airport',
  CMB: 'Bandaranaike International Airport',
  MLE: 'Velana International Airport',
  KTM: 'Tribhuvan International Airport',
  DAC: 'Hazrat Shahjalal International Airport',
  PBH: 'Paro International Airport',
  SYD: 'Sydney Kingsford Smith Airport',
  MEL: 'Melbourne Airport',

  // ── Abroad: Europe and the Americas ─────────────────────────────────────
  LHR: 'London Heathrow Airport',
  LGW: 'London Gatwick Airport',
  STN: 'London Stansted Airport',
  LTN: 'London Luton Airport',
  LCY: 'London City Airport',
  CDG: 'Paris Charles de Gaulle Airport',
  ORY: 'Paris Orly Airport',
  FRA: 'Frankfurt Airport',
  AMS: 'Amsterdam Schiphol Airport',
  ZRH: 'Zurich Airport',
  FCO: 'Rome Fiumicino Airport',
  MXP: 'Milan Malpensa Airport',
  JFK: 'John F. Kennedy International Airport',
  EWR: 'Newark Liberty International Airport',
  LGA: 'LaGuardia Airport',
  ORD: "Chicago O'Hare International Airport",
  SFO: 'San Francisco International Airport',
  LAX: 'Los Angeles International Airport',
  YYZ: 'Toronto Pearson International Airport',
};
